/**
 * Condition ① — conceptual grammar families without dedicated business-plane routes.
 * Proxy to existing runtime surfaces + businessDomain alias for action/thread grammar.
 *
 * @see .cursor/plans/active/workbench/wb4-p3-final-execution-plan.md §1.1
 */

export type WorkbenchGrammarProxyTarget = {
  /** Route family for PTG/runtime shell (domain top segment). */
  domainTop: string;
  /** List/detail subdomain segment. */
  subdomain: string;
  /** Canonical businessDomain on the proxied surface. */
  canonicalBusinessDomain: string;
};

/** Conceptual grammar family → proxied runtime route (no duplicate list/detail routes). */
export const WORKBENCH_DOMAIN_GRAMMAR_PROXY: Readonly<Record<string, WorkbenchGrammarProxyTarget>> =
  {
    customer: {
      domainTop: 'entity',
      subdomain: 'external',
      canonicalBusinessDomain: 'external_entity',
    },
    counterparty: {
      domainTop: 'entity',
      subdomain: 'external',
      canonicalBusinessDomain: 'external_entity',
    },
    order: {
      domainTop: 'manufacturing',
      subdomain: 'production-order',
      canonicalBusinessDomain: 'production_order',
    },
    sales_order: {
      domainTop: 'manufacturing',
      subdomain: 'production-order',
      canonicalBusinessDomain: 'production_order',
    },
    supplier: {
      domainTop: 'entity',
      subdomain: 'external',
      canonicalBusinessDomain: 'external_entity',
    },
    collection: {
      domainTop: 'finance',
      subdomain: 'receivables',
      canonicalBusinessDomain: 'finance_state',
    },
    ar_collection: {
      domainTop: 'finance',
      subdomain: 'receivables',
      canonicalBusinessDomain: 'finance_state',
    },
    /** Conceptual document grammar — proxied to knowledge subdomains (no /document/* routes). */
    document: {
      domainTop: 'knowledge',
      subdomain: 'report',
      canonicalBusinessDomain: 'knowledge_report',
    },
    document_report: {
      domainTop: 'knowledge',
      subdomain: 'report',
      canonicalBusinessDomain: 'knowledge_report',
    },
    document_policy: {
      domainTop: 'knowledge',
      subdomain: 'policy',
      canonicalBusinessDomain: 'knowledge_policy',
    },
    document_plan: {
      domainTop: 'knowledge',
      subdomain: 'plan',
      canonicalBusinessDomain: 'knowledge_plan',
    },
    org_document: {
      domainTop: 'knowledge',
      subdomain: 'report',
      canonicalBusinessDomain: 'knowledge_report',
    },
  } as const;

export function resolveWorkbenchGrammarProxy(
  businessDomain: string
): WorkbenchGrammarProxyTarget | null {
  return WORKBENCH_DOMAIN_GRAMMAR_PROXY[businessDomain.trim()] ?? null;
}

export function isWorkbenchGrammarProxyDomain(businessDomain: string): boolean {
  return resolveWorkbenchGrammarProxy(businessDomain) !== null;
}

/** Map grammar-domain inline persist keys onto proxied canonical entity fields. */
export function resolveGrammarProxyInlinePersistPatch(
  grammarDomain: string,
  fieldKey: string,
  value: string
): Record<string, string> {
  const proxy = resolveWorkbenchGrammarProxy(grammarDomain);
  if (!proxy) {
    return { [fieldKey]: value };
  }
  if (grammarDomain === 'collection' || grammarDomain === 'ar_collection') {
    if (fieldKey === 'followUpDate') {
      return { dueDate: value };
    }
    if (fieldKey === 'collectionNote') {
      return { internalNote: value };
    }
  }
  return { [fieldKey]: value };
}
